import {
  BookmarkInventory,
  InventoryData,
  InventoryIndicator,
  InventoryReference,
  InventorySpace,
  InventoryStatus,
  InventoryToPublish,
  InventoryWidget,
} from '../models/bookmark-inventory';
import { units } from '../models/unit';

/** Indicator data row with display names, for the data matrix. */
export interface InventoryDataRow extends InventoryData {
  indicatorName: string;
  yearLabel: string;
}

export interface InventoryView {
  widgets: InventoryWidget[];
  indicators: InventoryIndicator[];
  spaces: InventorySpace[];
  data: InventoryDataRow[];
  references: InventoryReference[];
}

export interface InventoryFilter {
  /** only what this widget uses */
  widgetId: string | null;
  /** only what isn't public (yet) or is broken */
  onlyProblems: boolean;
}

/**
 * Full indicator name as in the rest of the app
 * (`IndicatorStore.getFullName`): "Set: Name Einheit".
 */
export function indicatorFullName(
  indicator: Pick<InventoryIndicator, 'name' | 'setName' | 'unit'>
): string {
  const name = indicator.setName
    ? `${indicator.setName}: ${indicator.name}`
    : indicator.name;
  const unit = indicator.unit ? units[indicator.unit]?.short : '';
  return unit ? `${name} ${unit}` : name;
}

/** placeholder for widgets without a heading of their own */
export const WIDGET_WITHOUT_HEADLINE = 'Ohne Überschrift';

/** Display name of a widget: its own heading or the placeholder. */
export function widgetLabel(widget: Pick<InventoryWidget, 'headline'>): string {
  return widget.headline ?? WIDGET_WITHOUT_HEADLINE;
}

const usedBy = (widgetIds: string[], widgetId: string | null) =>
  !widgetId || widgetIds.includes(widgetId);

/** Is a data record a problem for a publication? */
const isDataProblem = (row: InventoryData) => row.state !== 'published';

/**
 * Narrows the inventory to one widget and/or to what still stands in the way
 * of a publication, and resolves names for the data rows.
 */
export function applyInventoryFilter(
  inventory: BookmarkInventory,
  filter: InventoryFilter
): InventoryView {
  const { widgetId, onlyProblems } = filter;

  const indicatorNames = new Map(
    inventory.indicators.map(indicator => [
      indicator._id,
      indicatorFullName(indicator),
    ])
  );
  const yearLabels = new Map<string, string>();
  inventory.spaces.forEach(space =>
    space.years.forEach(year =>
      yearLabels.set(year._id, `${year.year ?? '?'} · ${space.name}`)
    )
  );

  const widgets = inventory.widgets.filter(
    widget =>
      (!widgetId || widget.id === widgetId) &&
      (!onlyProblems || widget.issueCount > 0)
  );

  const indicators = inventory.indicators.filter(
    indicator =>
      usedBy(indicator.widgetIds, widgetId) &&
      (!onlyProblems || indicator.status !== 'ok' || indicator.hiddenPublicly)
  );

  const spaces = inventory.spaces
    .map(space => ({
      ...space,
      years: space.years.filter(
        year =>
          usedBy(year.widgetIds, widgetId) &&
          (!onlyProblems || !year.published || year.status !== 'ok')
      ),
    }))
    .filter(
      space =>
        space.years.length > 0 ||
        (usedBy(space.widgetIds, widgetId) &&
          space.widgetIds.length > 0 &&
          (!onlyProblems || space.status !== 'ok'))
    );

  const data = inventory.data
    .filter(
      row =>
        usedBy(row.widgetIds, widgetId) && (!onlyProblems || isDataProblem(row))
    )
    .map(row => ({
      ...row,
      indicatorName: indicatorNames.get(row.indicatorId) ?? row.indicatorId,
      yearLabel: yearLabels.get(row.dataLayerId) ?? row.dataLayerId,
    }))
    .sort(
      (a, b) =>
        a.indicatorName.localeCompare(b.indicatorName) ||
        a.yearLabel.localeCompare(b.yearLabel)
    );

  const references = inventory.references.filter(
    reference =>
      usedBy(reference.widgetIds, widgetId) &&
      (!onlyProblems ||
        reference.status !== 'ok' ||
        reference.published === false)
  );

  return { widgets, indicators, spaces, data, references };
}

/** Number of items "Fehlendes freigeben" would publish. */
export function countToPublish(toPublish: InventoryToPublish): number {
  return (
    toPublish.dataLayerIds.length +
    toPublish.indicatorDataIds.length +
    toPublish.topicIds.length +
    toPublish.storyIds.length
  );
}

/** Human-readable summary of what would be published, for the confirmation. */
export function describeToPublish(toPublish: InventoryToPublish): string[] {
  const plural = (count: number, one: string, many: string) =>
    `${count} ${count === 1 ? one : many}`;
  return [
    toPublish.dataLayerIds.length &&
      plural(toPublish.dataLayerIds.length, 'Jahr', 'Jahre'),
    toPublish.indicatorDataIds.length &&
      plural(toPublish.indicatorDataIds.length, 'Datensatz', 'Datensätze'),
    toPublish.topicIds.length &&
      plural(toPublish.topicIds.length, 'Thema', 'Themen'),
    toPublish.storyIds.length &&
      plural(toPublish.storyIds.length, 'Bericht', 'Berichte'),
  ].filter((entry): entry is string => !!entry);
}

/** Tag text and PrimeNG severity for a reference's existence status. */
export function statusTag(status: InventoryStatus): {
  label: string;
  severity: 'success' | 'warning' | 'danger' | 'info';
} {
  switch (status) {
    case 'deleted':
      return { label: 'gelöscht', severity: 'danger' };
    case 'missing':
      return { label: 'fehlt', severity: 'danger' };
    case 'foreign':
      return { label: 'andere Kommune', severity: 'danger' };
    default:
      return { label: 'vorhanden', severity: 'success' };
  }
}

/**
 * Tag text and PrimeNG severity for a data record. Blue ('info') stands for
 * "öffentlich" like everywhere else in the backend.
 */
export function dataStateTag(state: InventoryData['state']): {
  label: string;
  severity: 'info' | 'warning' | 'secondary';
} {
  switch (state) {
    case 'published':
      return { label: 'öffentlich', severity: 'info' };
    case 'internal':
      return { label: 'intern', severity: 'warning' };
    default:
      return { label: 'keine Daten', severity: 'secondary' };
  }
}
